import React, { useState } from 'react';
import { X, Save, CheckCircle, XCircle, AlertCircle } from 'lucide-react';
import { syncService } from '../services/api';

const ProductEditor = ({ product, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    marketplaceSku: product.marketplaceSku || '',
    marketplaceTitle: product.marketplaceTitle || '',
    marketplaceDescription: product.marketplaceDescription || '',
    marketplacePrice: product.marketplacePrice || '',
    marketplaceQuantity: product.marketplaceQuantity ?? '',
    marketplaceBrand: product.marketplaceBrand || '',
    marketplaceEan: product.marketplaceEan || '',
    marketplaceWeight: product.marketplaceWeight || '',
    marketplaceLength: product.marketplaceLength || '',
    marketplaceWidth: product.marketplaceWidth || '',
    marketplaceHeight: product.marketplaceHeight || '',
    marketplaceCategoryCode: product.marketplaceCategoryCode || '',
  });
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState(product.validationErrors || []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (status = null) => {
    try {
      setSaving(true);
      const dataToSave = { ...formData };

      if (dataToSave.marketplacePrice) {
        dataToSave.marketplacePrice = parseFloat(dataToSave.marketplacePrice);
      }
      if (dataToSave.marketplaceQuantity !== '') {
        dataToSave.marketplaceQuantity = parseInt(dataToSave.marketplaceQuantity, 10);
      }
      if (dataToSave.marketplaceWeight) {
        dataToSave.marketplaceWeight = parseFloat(dataToSave.marketplaceWeight);
      }
      if (dataToSave.marketplaceLength) {
        dataToSave.marketplaceLength = parseFloat(dataToSave.marketplaceLength);
      }
      if (dataToSave.marketplaceWidth) {
        dataToSave.marketplaceWidth = parseFloat(dataToSave.marketplaceWidth);
      }
      if (dataToSave.marketplaceHeight) {
        dataToSave.marketplaceHeight = parseFloat(dataToSave.marketplaceHeight);
      }

      if (status) {
        dataToSave.stagingStatus = status;
      }

      const response = await syncService.updateStagedProduct(product.id, dataToSave);
      setValidationErrors(response.data.validationErrors || []);

      if (status || response.data.validationStatus === 'VALID') {
        onSave();
      }
    } catch (error) {
      console.error('Save failed:', error);
      alert('Failed to save. Check console for details.');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkReady = () => handleSave('READY');
  const handleExclude = () => handleSave('EXCLUDED');

  const isValid = validationErrors.length === 0 && formData.marketplaceSku && formData.marketplaceQuantity !== '';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Edit Product</h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="modal-body">
          {validationErrors.length > 0 && (
            <div style={styles.validationBox}>
              <AlertCircle size={16} color="#ff3b30" />
              <div style={styles.validationContent}>
                <span style={styles.validationTitle}>Missing required fields</span>
                <ul style={styles.validationList}>
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error.replace('Missing required field: ', '')}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

          <div style={styles.sourceInfo}>
            <span style={styles.sourceLabel}>From WooCommerce</span>
            <span style={styles.sourceName}>{product.wooName}</span>
            {product.wooSku && (
              <span style={styles.sourceDetail}>SKU: {product.wooSku}</span>
            )}
          </div>

          <div style={styles.formGrid}>
            <div className="form-group">
              <label className="form-label">SKU *</label>
              <input
                type="text"
                name="marketplaceSku"
                value={formData.marketplaceSku}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceSku ? 'form-input-error' : ''}`}
                placeholder="e.g., BIKO-SOFA-001"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Quantity *</label>
              <input
                type="number"
                name="marketplaceQuantity"
                value={formData.marketplaceQuantity}
                onChange={handleChange}
                className={`form-input ${formData.marketplaceQuantity === '' ? 'form-input-error' : ''}`}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Price (ZAR) *</label>
              <input
                type="number"
                name="marketplacePrice"
                value={formData.marketplacePrice}
                onChange={handleChange}
                className="form-input"
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Category *</label>
              <select
                name="marketplaceCategoryCode"
                value={formData.marketplaceCategoryCode}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceCategoryCode ? 'form-input-error' : ''}`}
              >
                <option value="">Select a category</option>
                <option value="18876">Sofas</option>
                <option value="18877">Corner Couches</option>
                <option value="18878">Chairs</option>
                <option value="18881">Sleeper Couches</option>
                <option value="18886">Dining Sets</option>
              </select>
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Title</label>
              <input
                type="text"
                name="marketplaceTitle"
                value={formData.marketplaceTitle}
                onChange={handleChange}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Brand</label>
              <input
                type="text"
                name="marketplaceBrand"
                value={formData.marketplaceBrand}
                onChange={handleChange}
                className="form-input"
                placeholder="e.g., Delicate Couches"
              />
            </div>

            <div className="form-group">
              <label className="form-label">EAN / Barcode</label>
              <input
                type="text"
                name="marketplaceEan"
                value={formData.marketplaceEan}
                onChange={handleChange}
                className="form-input"
                placeholder="13-digit barcode"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Weight (kg) *</label>
              <input
                type="number"
                name="marketplaceWeight"
                value={formData.marketplaceWeight}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceWeight ? 'form-input-error' : ''}`}
                placeholder="0.00"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Length (cm) *</label>
              <input
                type="number"
                name="marketplaceLength"
                value={formData.marketplaceLength}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceLength ? 'form-input-error' : ''}`}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Width (cm) *</label>
              <input
                type="number"
                name="marketplaceWidth"
                value={formData.marketplaceWidth}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceWidth ? 'form-input-error' : ''}`}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Height (cm) *</label>
              <input
                type="number"
                name="marketplaceHeight"
                value={formData.marketplaceHeight}
                onChange={handleChange}
                className={`form-input ${!formData.marketplaceHeight ? 'form-input-error' : ''}`}
                placeholder="0"
                min="0"
              />
            </div>

            <div className="form-group" style={{ gridColumn: 'span 2' }}>
              <label className="form-label">Description</label>
              <textarea
                name="marketplaceDescription"
                value={formData.marketplaceDescription}
                onChange={handleChange}
                className="form-input"
                rows={3}
                style={{ resize: 'vertical' }}
              />
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={handleExclude} disabled={saving}>
            <XCircle size={16} />
            Exclude
          </button>
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={() => handleSave()} disabled={saving}>
            <Save size={16} />
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button className="btn btn-primary" onClick={handleMarkReady} disabled={saving || !isValid}>
            <CheckCircle size={16} />
            Mark as Ready
          </button>
        </div>
      </div>
    </div>
  );
};

const styles = {
  validationBox: {
    display: 'flex',
    gap: '12px',
    padding: '14px 16px',
    background: 'rgba(255, 59, 48, 0.08)',
    borderRadius: '10px',
    marginBottom: '20px',
  },
  validationContent: {
    flex: 1,
  },
  validationTitle: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#d70015',
  },
  validationList: {
    margin: '6px 0 0 0',
    paddingLeft: '16px',
    fontSize: '13px',
    color: '#d70015',
  },
  sourceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '14px 16px',
    background: '#f5f5f7',
    borderRadius: '10px',
    marginBottom: '20px',
  },
  sourceLabel: {
    fontSize: '11px',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: '#86868b',
  },
  sourceName: {
    fontSize: '14px',
    fontWeight: '500',
    color: '#1d1d1f',
  },
  sourceDetail: {
    fontSize: '13px',
    color: '#6e6e73',
    fontFamily: 'SF Mono, Monaco, Consolas, monospace',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
};

export default ProductEditor;